import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { ContentItem } from "@/components/dashboard/content-card/types";
import { fetchContentItem } from "./fetchContentItem";
import { useContentNavigation } from "./useContentNavigation";
import { usePrefetchRelated } from "./usePrefetchRelated";
import { useRedirectIfNotFound } from "./useRedirectIfNotFound";
import { useReWebClipContent } from "@/hooks/useReWebClipContent";
import { toast } from "sonner";

interface UseFocusedContentResult {
  id: string | undefined;
  contentItem: ContentItem | null;
  isLoading: boolean;
  refetch: () => Promise<unknown>;
  handleRelatedContentClick: (clickedId: string) => void;
  isReWebClipping: boolean;
  handleReWebClip: () => Promise<any>;
  isExtensionAvailable: boolean | null;
}

export const useFocusedContent = (): UseFocusedContentResult => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(true);
  const [hasAttemptedLoad, setHasAttemptedLoad] = useState(false);
  
  // Import navigation functionality from the separate hook
  const { navigationInProgress, handleRelatedContentClick } = useContentNavigation(id);
  
  // Import re-web-clip functionality
  const { isProcessing: isReWebClipping, reWebClipContent, isExtensionAvailable } = useReWebClipContent();
  
  // Prefetch related content items for faster navigation
  usePrefetchRelated(user?.id, id);

  // Log for navigation debugging
  useEffect(() => {
    console.log("[NAVIGATION DEBUG] useFocusedContent hook initialized with ID:", id);
  }, [id]);

  // Content fetching query with improved staleTime settings for better refresh behavior
  const { data: contentItem, refetch } = useQuery({
    queryKey: ["contentItem", id],
    queryFn: async () => {
      if (!id || !user) return null;
      setIsLoading(true);
      
      try {
        const result = await fetchContentItem(id, user.id, queryClient);
        
        // Log color information to help debug issues
        console.log(`[COLOR DEBUG] Query result for ${id}:`, {
          id: result?.id,
          title: result?.title,
          collection_color: result?.collection_color,
          collection_id: result?.collection_id,
          description: result?.description,
          hasDescription: !!result?.description
        });
        
        return result;
      } catch (error: any) {
        console.error('[NAVIGATION DEBUG] Error fetching content:', error.message);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    enabled: !!id && !!user,
    retry: 3,
    staleTime: 0, // Don't use stale data to ensure we always have fresh content
    gcTime: 300000,
    refetchOnWindowFocus: true, // Ensure we get updated data when the window regains focus
    refetchOnMount: 'always' // Always refetch when the component mounts
  });

  // Mark that we've attempted to load content
  useEffect(() => {
    if (!isLoading) {
      setHasAttemptedLoad(true);
    }
  }, [isLoading]);
  
  // Log color information changes for debugging
  useEffect(() => {
    if (contentItem) {
      console.log(`[COLOR DEBUG] Content item updated in useFocusedContent:`, {
        id: contentItem.id,
        title: contentItem.title,
        description: contentItem.description,
        hasDescription: !!contentItem.description,
        collection_color: contentItem.collection_color,
        collection_id: contentItem.collection_id
      });
    }
  }, [contentItem]);

  // Handle redirect if content is not found
  useRedirectIfNotFound(id, contentItem, isLoading, hasAttemptedLoad, navigationInProgress);

  // Updated re-web-clip handler to use soft level by default
  const handleReWebClip = async () => {
    if (!id || isReWebClipping) return Promise.resolve({ success: false, error: "Already processing or missing ID" });
    
    console.log("Initiating soft re-web-clip for content:", id);
    try {
      // Use soft re-clipping by default for the Focus page
      const result = await reWebClipContent(id, 'soft');
      
      if (!result.success) {
        console.error("Soft re-web-clip failed:", result.error);
      }
      
      return result;
    } catch (error) {
      console.error("Error in soft re-web-clip:", error);
      return { success: false, error };
    }
  };

  return {
    id,
    contentItem: contentItem as ContentItem | null,
    isLoading,
    refetch,
    handleRelatedContentClick,
    isReWebClipping,
    handleReWebClip,
    isExtensionAvailable
  };
};
